import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DiningRestaurantOption,
  DiningRoomI,
  DiningReservationAnalyticsStepName,
  ItineraryDay,
  SelectedRestaurantReservation,
  DiningMultistepDrawerProps,
} from '@msc-b2c/interfaces/interfaces';
import {
  addDaysAtSea,
  RootState,
  replacePlaceholders,
  formatTime,
  specialtyRestaurants,
} from '@msc-b2c/shared-utils';
import { Drawer } from '../../../../drawer/Drawer';
import { PaxSelectionStep } from './steps/PaxSelectionStep/PaxSelectionStep';
import { ChooseWhenBookStep } from './steps/ChooseWhenBookStep/ChooseWhenBookStep';
import { DateSelectionStep } from './steps/DateSelectionStep/DateSelectionStep';
import { TimeSelectionStep } from './steps/TimeSelectionStep/TimeSelectionStep';
import { ReservationRecapStep } from './steps/ReservationRecapStep/ReservationRecapStep';
import { CartReservationRecapStep } from './steps/CartReservationRecapStep/CartReservationRecapStep';
import { initializeDiningFlowState, transitionDiningFlow } from './DiningMultistepFlow';
import {
  DINING_DAY_AT_SEA_PORT_CODE,
  DiningFlowEntryPoint,
  DiningFlowEventType,
  DiningReservationMode,
  DiningStepId,
  ChooseWhenBookBannerVariant,
} from '@msc-b2c/interfaces/enums';
import styles from './diningMultistepDrawer.module.scss';
import ImageTitle from '../ImageTitle';
import {
  getAlreadyBookedPassengerId,
  isPriceTypeNotAllowed,
} from '../../../utils/PostBookingResultsUtilities';
import { buildDayLabel } from './utils/diningDateUtils';
import {
  buildDateAvailabilityMap,
  buildDiningRoomDateAvailabilityKey,
  buildMergedAvailabilityMapWithThematicValidation,
  getTimeslotsForDate,
  mergeFallbackTimesWithThematicAvailability,
  normalizeTimeSlots,
} from './utils/diningAvailabilityUtils';
import { fingerprintDiningReservationList } from './utils/diningReservationFingerprint';
import { useDiningAvailability } from './hooks/useDiningAvailability';
import { useDiningReservationSelection } from './hooks/useDiningReservationSelection';
import DiningModal from './DiningModal';
import { useDiningModal } from './hooks/useDiningModal';
import { useFocusTrap } from '../../../../drawer/useFocusTrap';
import {
  resolveChooseWhenBookInfoBgColor,
  resolveChooseWhenBookInfoBody,
  resolveChooseWhenBookInfoFontColor,
  resolveChooseWhenBookInfoIcon,
  resolveChooseWhenBookInfoTitle,
} from './utils/diningChooseWhenBookBannerResolvers';
import {
  buildDiningSelectionForCart as buildDiningSelectionForCartMetadata,
  sortReservationsChronologically,
} from './utils/diningSelectionMapper';
import { useDiningPaxEditNavigation } from './hooks/useDiningPaxEditNavigation';
import { useDiningPrimaryActionCommand } from './hooks/useDiningPrimaryActionCommand';
import { useDiningDrawerFooterConfig } from './hooks/useDiningDrawerFooterConfig';
import { useDiningPaxHeadsUpMessage } from './hooks/useDiningPaxHeadsUpMessage';
import { triggerDiningReservationStepEvent } from '../../../../utils/myAreaAnalytics';
import { getRestaurantInfoFromGraphql } from './utils/diningRestaurantInfoFromCF';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * DiningMultistepDrawer
 *
 * Self-contained drawer for the dining reservation multi-step flow.
 * It owns state via useReducer (no Redux for flow navigation – avoids coupling).
 * It renders the existing Drawer shell unchanged and manages its own content and
 * footer callbacks independently.
 *
 * The standard Drawer component is used with zero modifications.
 * All step-level logic (back button, CTA state, content) is managed here.
 */
export const DiningMultistepDrawer: React.FC<DiningMultistepDrawerProps> = ({
  isDrawerOpen,
  booking,
  onClose,
  onDrawerAbandonConfirmed,
  flowConfig,
  seedContext,
  onConfirmPassengers,
  onApply,
  onReservationModeSelected,
  postBookingWrapperID,
  setIsConfirmationDrawer,
  preserveSelectionGoingBack,
  cartReservationRecapProps: cartReservationRecapPropsOverride,
  passengerModificationBannerProps,
  onPersistDiningSelectionForAlreadyPurchased,
  setEditedDiningPackage,
}) => {
  const [flowState, dispatch] = useReducer(
    transitionDiningFlow,
    flowConfig,
    initializeDiningFlowState
  );
  const hasTrackedBeginReservationPresentation = useRef(false);
  const hasTrackedDateTimeStepPresentation = useRef(false);

  const diningProps = useSelector((state: RootState) => state.myAreaCommonConfig.diningProps);
  const drawerProps = useSelector((state: RootState) => state.myAreaCommonConfig.drawerProps);

  const reduxDispatch = useDispatch();

  const specialityRestaurantsFromCf = useSelector(
    (state: RootState) => state.myAreaCommonConfig.specialtyRestaurants
  );

  const diningHeadsUpMessagesConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig.diningHeadsUpMessagesConfig
  );
  const drawerExcursionAEMConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig?.drawerExcursionProps?.drawerExcursionType
  );
  const isLayerExcursionOpen = useSelector(
    (state: RootState) => state.myAreaDrawers.isLayerMyAreaExcursionOpen
  );
  const myAreaExcursionCardData = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaExcursionCard
  );
  const myAreaOBSCardData = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaOBSCard
  );
  const shopOBSData = useSelector((state: RootState) => state.myCruise?.shopOBS ?? null);
  const myCruiseItinerary = useSelector((state: RootState) => state.myCruise?.itinerary ?? null);
  const itineraryDetailsData = useSelector(
    (state: RootState) => state.itineraryDetails?.itineraryDetailsData ?? null
  );
  const cartItems = useSelector((state: RootState) => state.myAreaCart.items);
  const obsTitle = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaOBSCard?.description
  );

  const [passengersToAdd, setPassengersToAdd] = React.useState<Record<
    string,
    Record<string, boolean>
  > | null>(seedContext?.preselectedPassengers ?? null);
  const [persistedPassengersSelection, setPersistedPassengersSelection] = React.useState<Record<
    string,
    Record<string, boolean>
  > | null>(seedContext?.preselectedPassengers ?? null);

  const [isProcessing, setIsProcessing] = React.useState(false);
  const [hasAttemptedDateTimeBootstrap, setHasAttemptedDateTimeBootstrap] = useState(false);
  const [chooseWhenBookBannerVariant, setChooseWhenBookBannerVariant] =
    useState<ChooseWhenBookBannerVariant>(ChooseWhenBookBannerVariant.NONE);

  const { modal, openModal, closeModal } = useDiningModal();

  // While the close-confirmation modal is shown it takes the focus trap over
  // from the drawer, and gives it back when it closes.
  const confirmationTrapRef = useRef<HTMLDivElement>(null);
  useFocusTrap(confirmationTrapRef, Boolean(modal?.show));

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isDrawerOpen) {
      return;
    }

    setPersistedPassengersSelection(seedContext?.preselectedPassengers ?? null);
  }, [isDrawerOpen, seedContext?.preselectedPassengers]);

  const { currentStep } = flowState;
  const isCartOrAlreadyPurchasedFlow =
    flowConfig.entryPoint === DiningFlowEntryPoint.CART ||
    flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED;

  const showPriceInFooter =
    currentStep === DiningStepId.PAX_SELECTION ||
    currentStep === DiningStepId.CHOOSE_WHEN_BOOK ||
    (currentStep === DiningStepId.DATE_TIME_SELECTION && !isCartOrAlreadyPurchasedFlow) ||
    currentStep === DiningStepId.RESERVATION_RECAP;

  useEffect(() => {
    const isPaxSelectionStep =
      currentStep === DiningStepId.PAX_SELECTION || currentStep === DiningStepId.PAX_SELECTION_EDIT;

    if (!isPaxSelectionStep) {
      return;
    }

    setIsDiningButtonLoading(false);
  }, [currentStep]);

  useEffect(() => {
    if (specialityRestaurantsFromCf.length === 0) {
      getRestaurantInfoFromGraphql().then((restaurantItems) => {
        if (restaurantItems) {
          reduxDispatch(specialtyRestaurants(restaurantItems));
        }
      });
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Compute itinerary and availability
  // ---------------------------------------------------------------------------

  const itineraryDays = useMemo(() => {
    if (!booking?.cabins[0]?.cruise) return [];

    const itinerarySource = myCruiseItinerary ?? itineraryDetailsData;
    const departureDate = new Date(
      itinerarySource?.departureDate ?? booking.cabins[0].cruise.departureDate
    );

    // Prefer real itinerary entries when available so short cruises do not render padded day cards.
    const sourceDays = itinerarySource?.days ?? [];
    if (
      itinerarySource != null &&
      sourceDays.length > 0 &&
      !Number.isNaN(departureDate.getTime())
    ) {
      const itineraryDaysWithSea = addDaysAtSea(
        itinerarySource,
        diningProps?.diningDateTimeProps?.dayAtSeaLabel ?? ''
      ).days;
      const uniqueByDate = new Map<string, Omit<ItineraryDay, 'isStartDay' | 'isEndDay'>>();

      itineraryDaysWithSea.forEach((day) => {
        const parsedDate = new Date(day.date);
        if (Number.isNaN(parsedDate.getTime())) {
          return;
        }

        const date = parsedDate.toISOString().split('T')[0];
        if (uniqueByDate.has(date)) {
          return;
        }

        const diffMs = parsedDate.getTime() - departureDate.getTime();
        const dayNumber = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
        const portName = day.port?.name;
        const isAtSea =
          day.port?.code === DINING_DAY_AT_SEA_PORT_CODE ||
          ('dayAtSea' in day && Boolean((day as { dayAtSea?: boolean }).dayAtSea)) ||
          !portName;

        uniqueByDate.set(date, {
          dayNumber,
          date,
          portName,
          isAtSea,
          label: buildDayLabel(
            dayNumber,
            diningProps?.diningDateTimeProps?.dayLabel ?? '',
            diningProps?.diningDateTimeProps?.dayAtSeaLabel ?? '',
            portName,
            isAtSea
          ),
        });
      });

      const realItineraryDays = Array.from(uniqueByDate.values())
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((day, index, all) => ({
          ...day,
          isStartDay: index === 0,
          isEndDay: index === all.length - 1,
        }));

      if (realItineraryDays.length > 0) {
        return realItineraryDays;
      }
    }

    return [];
  }, [
    booking?.cabins,
    diningProps?.diningDateTimeProps?.dayAtSeaLabel,
    diningProps?.diningDateTimeProps?.dayLabel,
    myCruiseItinerary,
    itineraryDetailsData,
  ]);

  const selectedPassengerIds = useMemo(() => {
    if (!passengersToAdd) {
      return [];
    }

    return Array.from(
      new Set(
        Object.values(passengersToAdd).flatMap((cabin) =>
          Object.entries(cabin)
            .filter(([, isSelected]) => isSelected)
            .map(([passengerId]) => passengerId)
        )
      )
    );
  }, [passengersToAdd]);

  const [isDiningButtonLoading, setIsDiningButtonLoading] = useState(false);

  const serializePassengersSelection = useCallback(
    (selection?: Record<string, Record<string, boolean>> | null): string => {
      if (!selection) {
        return '';
      }

      const normalized = Object.entries(selection)
        .sort(([cabinA], [cabinB]) => cabinA.localeCompare(cabinB))
        .map(([cabinId, passengers]) => {
          const selectedPassengerIds = Object.entries(passengers)
            .filter(([, isSelected]) => isSelected)
            .map(([passengerId]) => passengerId)
            .sort();

          return `${cabinId}:${selectedPassengerIds.join(',')}`;
        })
        .filter((entry) => !entry.endsWith(':'));

      return normalized.join('|');
    },
    []
  );

  const {
    thematicSlots,
    thematicSlotsByDiningRoomDate,
    availableDiningRooms,
    maximumSlotsBookable,
    isLoadingAvailability,
    hasResolvedInitialThematicAvailability,
    hasInitialAvailabilityServiceErrorFallback,
    availabilityError,
    isReserveNowDisabled,
    setAvailabilityError,
    callDiningRoomReservations,
    fetchInitialThematicAvailability,
    prefetchINT135DiningRoomMetadata,
  } = useDiningAvailability({
    shouldUseApiMocks: false,
    itineraryDays,
    seedDiningRoomCode: seedContext?.preselectedDiningRoomCode,
    shopObsData:
      String(myAreaOBSCardData?.code ?? '') === String(shopOBSData?.code ?? '')
        ? shopOBSData
        : null,
    selectedPassengerIds,
    isLayerExcursionOpen,
    excursionObsCode: myAreaExcursionCardData?.code,
    myAreaObsCode: myAreaOBSCardData?.code,
    entryPoint: flowConfig.entryPoint,
    persistedMaximumSlotsBookable: seedContext?.maximumSlotsBookable,
    alreadyPurchasedObsCard: myAreaOBSCardData,
  });

  const seededSelectedRestaurants = useMemo<SelectedRestaurantReservation[]>(() => {
    const seedReservations = seedContext?.preselectedReservations ?? [];

    return seedReservations
      .filter((reservation) =>
        Boolean(reservation.day && reservation.hour && reservation.restaurantCode)
      )
      .map((reservation) => {
        const itineraryDay = itineraryDays.find((day) => day.date === reservation.day);

        return {
          restaurantCode: reservation.restaurantCode,
          restaurantName: reservation.restaurantName,
          date: reservation.day,
          time: reservation.hour,
          dayNumber: itineraryDay?.dayNumber,
          portName: itineraryDay?.portName,
          isAtSea: itineraryDay?.isAtSea,
        };
      });
  }, [itineraryDays, seedContext?.preselectedReservations]);

  const isAddReservationFlow = (
    currentStep: DiningStepId,
    editingReservationIndex: number | null
  ) => {
    return (
      currentStep === DiningStepId.DATE_TIME_SELECTION &&
      Number.isNaN(editingReservationIndex ?? Number.NaN)
    );
  };

  const {
    selectedDate,
    selectedRestaurantCode,
    selectedTime,
    selectedRestaurants,
    editingReservationIndex,
    isConfirming,
    isCommittingSelection,
    isFooterDrivenDateTimeApplyReady,
    selectedRestaurantsCountByCode,
    handleSelectDate,
    handleSelectTime,
    handleSelectRestaurantTime,
    handleConfirmDateTimeSelection,
    handleEditReservation,
    handleRemoveReservation,
    handleAddAnotherReservation,
    resetReservationsForPassengerEdit,
  } = useDiningReservationSelection({
    initialSelectedDate: seedContext?.preselectedDate,
    initialSelectedDiningRoomCode: seedContext?.preselectedDiningRoomCode,
    initialSelectedTime: seedContext?.preselectedTime,
    initialSelectedRestaurants: seededSelectedRestaurants,
    availableDiningRooms,
    maximumSlotsBookable,
    itineraryDays,
    callDiningRoomReservations,
    onSetAvailabilityError: setAvailabilityError,
    onReservationSaved: async ({
      nextSelectedRestaurants,
      wasEditingReservation,
      savedReservation: _savedReservation,
    }) => {
      setChooseWhenBookBannerVariant(ChooseWhenBookBannerVariant.NONE);
      if (flowConfig.entryPoint === DiningFlowEntryPoint.CART) {
        if (wasEditingReservation) {
          triggerDiningReservationAnalyticsStep('confirmation', {
            availabilityRestaurants:
              getDiningAnalyticsRestaurantsForReservations(nextSelectedRestaurants),
            selectedReservations: nextSelectedRestaurants,
            isReservationUpdate: true,
          });
        }

        const hasMultipleReservableSlots =
          (seedContext?.maximumSlotsBookable ?? maximumSlotsBookable) > 1;
        const hasStillReservationToDo =
          (seedContext?.maximumSlotsBookable ?? maximumSlotsBookable) >
          nextSelectedRestaurants.length;
        const shouldOpenReservationRecapInCart =
          hasMultipleReservableSlots && hasStillReservationToDo && !wasEditingReservation;

        setIsProcessing(false);

        if (shouldOpenReservationRecapInCart) {
          dispatch({
            type: DiningFlowEventType.SELECT_RESERVATION_MODE,
            mode: DiningReservationMode.WITH_TIMESLOT,
          });
          dispatch({ type: DiningFlowEventType.NEXT });
          return;
        }

        dispatch({ type: DiningFlowEventType.BACK });
        return;
      }

      if (flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED) {
        const hasPreviousSelectionAtSave = seededSelectedRestaurants.length > 0;

        if (onPersistDiningSelectionForAlreadyPurchased !== undefined) {
          const diningSelection = getDiningSelectionForCart(nextSelectedRestaurants);
          onConfirmPassengers(
            passengersToAdd ?? {},
            DiningStepId.DATE_TIME_SELECTION,
            diningSelection
          );
          await onPersistDiningSelectionForAlreadyPurchased(passengersToAdd ?? {}, diningSelection);
        }

        triggerDiningReservationAnalyticsStep('confirmation', {
          availabilityRestaurants:
            getDiningAnalyticsRestaurantsForReservations(nextSelectedRestaurants),
          selectedReservations: nextSelectedRestaurants,
          isReservationUpdate: wasEditingReservation,
        });
        setIsProcessing(false);

        // Ensure recap step is active before advancing from DATE_TIME_SELECTION.
        dispatch({
          type: DiningFlowEventType.SELECT_RESERVATION_MODE,
          mode: DiningReservationMode.WITH_TIMESLOT,
        });

        if (!hasPreviousSelectionAtSave) {
          // no previous selection: first save lands on RESERVATION_RECAP.
          dispatch({ type: DiningFlowEventType.NEXT });
          return;
        }

        // Previous selection exists: return to CART_RESERVATION_RECAP.
        dispatch({ type: DiningFlowEventType.BACK });
        return;
      }

      triggerDiningReservationAnalyticsStep('confirmation', {
        availabilityRestaurants:
          getDiningAnalyticsRestaurantsForReservations(nextSelectedRestaurants),
        selectedReservations: nextSelectedRestaurants,
      });
      setIsProcessing(false);
      dispatch({ type: DiningFlowEventType.NEXT });
    },
    onReservationSaveFailed: () => {
      if (flowConfig.entryPoint === DiningFlowEntryPoint.CART) {
        setIsProcessing(false);
        dispatch({ type: DiningFlowEventType.BACK });
        return;
      }

      if (flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED) {
        setIsProcessing(false);
        dispatch({ type: DiningFlowEventType.BACK });
        return;
      }

      setChooseWhenBookBannerVariant(ChooseWhenBookBannerVariant.GENERIC);
      dispatch({ type: DiningFlowEventType.EDIT_CHOOSE_WHEN_BOOK });
    },
    onEditDateTimeRequested: () => {
      if (
        flowConfig.entryPoint === DiningFlowEntryPoint.CART ||
        flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED
      ) {
        dispatch({
          type: DiningFlowEventType.SELECT_RESERVATION_MODE,
          mode: DiningReservationMode.WITH_TIMESLOT,
        });
        onReservationModeSelected?.(DiningReservationMode.WITH_TIMESLOT);
      }
      dispatch({ type: DiningFlowEventType.EDIT_DATE_TIME });
    },
    onSingleSlotRemoved: () => {
      dispatch({ type: DiningFlowEventType.EDIT_CHOOSE_WHEN_BOOK });
    },
    cartSeedReservationsForApplyGate: seededSelectedRestaurants,
    shouldValidateSlotAvailabilityOnConfirm:
      flowConfig.entryPoint !== DiningFlowEntryPoint.ALREADY_PURCHASED,
    onBeforeReservationCommit: () => {
      setIsProcessing(
        (seedContext?.preselectedReservations ?? []).length > 0 &&
          !isAddReservationFlow(currentStep, editingReservationIndex)
      );
    },
  });

  const selectedDays = useMemo(() => {
    return selectedRestaurants.reduce<number[]>((values, restaurant) => {
      if (!restaurant?.dayNumber) {
        return values;
      }

      // Keep duplicates to distinguish "same day selected once" vs "same day selected multiple times".
      values.push(restaurant.dayNumber);
      return values;
    }, []);
  }, [selectedRestaurants]);

  const editingReservationDayNumber = useMemo(() => {
    if (editingReservationIndex === null) {
      return undefined;
    }

    return selectedRestaurants[editingReservationIndex]?.dayNumber;
  }, [editingReservationIndex, selectedRestaurants]);

  const availabilityMap = useMemo(() => {
    const bootstrapMap = buildDateAvailabilityMap(thematicSlots);
    const itineraryDates = itineraryDays.map((d) => d.date);
    const roomCodes = availableDiningRooms.map((r) => r.code);

    return buildMergedAvailabilityMapWithThematicValidation({
      bootstrapAvailabilityMap: bootstrapMap,
      itineraryDates,
      availableDiningRoomCodes: roomCodes,
      thematicSlotsByDiningRoomDate,
    });
  }, [thematicSlots, itineraryDays, availableDiningRooms, thematicSlotsByDiningRoomDate]);

  const selectedDayTimeslots = useMemo(() => {
    if (!selectedDate) {
      return [];
    }

    const fallbackDiningRoom =
      (seedContext?.preselectedDiningRoomCode
        ? availableDiningRooms.find((room) => room.code === seedContext.preselectedDiningRoomCode)
        : availableDiningRooms[0]) ?? undefined;
    const thematicRoomDateKey = buildDiningRoomDateAvailabilityKey(
      fallbackDiningRoom?.code,
      selectedDate
    );
    const thematicTimes = thematicRoomDateKey
      ? thematicSlotsByDiningRoomDate[thematicRoomDateKey] ?? []
      : getTimeslotsForDate(selectedDate, availabilityMap);
    const mergedTimeslots = mergeFallbackTimesWithThematicAvailability({
      fallbackTimes: normalizeTimeSlots(fallbackDiningRoom?.timeSlots),
      thematicTimes,
      isThematicAvailabilityResolved: hasResolvedInitialThematicAvailability,
    });

    return mergedTimeslots;
  }, [
    availabilityMap,
    availableDiningRooms,
    hasResolvedInitialThematicAvailability,
    seedContext?.preselectedDiningRoomCode,
    selectedDate,
    thematicSlotsByDiningRoomDate,
  ]);

  const getRestaurantOptionsForDate = useCallback(
    (date?: string): DiningRestaurantOption[] => {
      if (!date || availableDiningRooms.length === 0) {
        return [];
      }

      return availableDiningRooms
        .filter((room): room is DiningRoomI & { code: string } => Boolean(room.code))
        .map((room) => {
          const conflictTimes = selectedRestaurants
            .filter((reservation, reservationIndex) => {
              if (
                editingReservationIndex !== null &&
                reservationIndex === editingReservationIndex
              ) {
                return false;
              }
              return reservation.date === date;
            })
            .map((reservation) => reservation.time);

          const currentReservationsForRoom = selectedRestaurantsCountByCode[room.code] ?? 0;
          const perRestaurantLimit = Math.max(
            1,
            room.maximumBookingLimit ?? room.allowedQuantity ?? maximumSlotsBookable
          );
          const isLimitReached = currentReservationsForRoom >= perRestaurantLimit;
          const thematicRoomDateKey = buildDiningRoomDateAvailabilityKey(room.code, date);
          const thematicRoomTimeslots = thematicRoomDateKey
            ? thematicSlotsByDiningRoomDate[thematicRoomDateKey] ?? []
            : [];
          const mergedTimeslots = mergeFallbackTimesWithThematicAvailability({
            fallbackTimes: normalizeTimeSlots(room.timeSlots),
            thematicTimes: thematicRoomTimeslots,
            isThematicAvailabilityResolved: hasResolvedInitialThematicAvailability,
          });

          return {
            code: room.code,
            name: room.description,
            category: '',
            maximumBookingLimit: room.maximumBookingLimit ?? room.allowedQuantity,
            isLimitReached,
            conflictTimes,
            timeslots: mergedTimeslots,
          };
        });
    },
    [
      editingReservationIndex,
      availableDiningRooms,
      selectedRestaurants,
      selectedRestaurantsCountByCode,
      maximumSlotsBookable,
      hasResolvedInitialThematicAvailability,
      thematicSlotsByDiningRoomDate,
    ]
  );

  const restaurantOptionsForSelectedDate = useMemo<DiningRestaurantOption[]>(() => {
    return getRestaurantOptionsForDate(selectedDate);
  }, [getRestaurantOptionsForDate, selectedDate]);

  const getFallbackRestaurantOptionsForDate = useCallback(
    (date?: string): DiningRestaurantOption[] => {
      if (!date) {
        return [];
      }

      const restaurantOptions = getRestaurantOptionsForDate(date);
      if (restaurantOptions.length > 0) {
        return restaurantOptions;
      }

      const activeRestaurantCode =
        (isLayerExcursionOpen ? myAreaExcursionCardData?.code : myAreaOBSCardData?.code) ?? '';

      if (!activeRestaurantCode) {
        return [];
      }

      const activeFallbackDiningRoom = availableDiningRooms.find(
        (room) => room.code === activeRestaurantCode
      );
      const fallbackTimeslots = mergeFallbackTimesWithThematicAvailability({
        fallbackTimes: normalizeTimeSlots(activeFallbackDiningRoom?.timeSlots),
        thematicTimes: [],
        isThematicAvailabilityResolved: hasResolvedInitialThematicAvailability,
      });

      return [
        {
          code: activeRestaurantCode,
          name: obsTitle,
          timeslots: fallbackTimeslots,
        },
      ];
    },
    [
      availableDiningRooms,
      getRestaurantOptionsForDate,
      hasResolvedInitialThematicAvailability,
      isLayerExcursionOpen,
      myAreaExcursionCardData?.code,
      myAreaOBSCardData?.code,
      obsTitle,
    ]
  );

  const getDiningAnalyticsSelectedDay = useCallback(
    (date?: string): string => {
      if (!date) {
        return '';
      }

      return itineraryDays.find((day) => day.date === date)?.dayNumber?.toString() ?? '';
    },
    [itineraryDays]
  );

  const getDiningAnalyticsRestaurantsForReservations = useCallback(
    (reservations: ReadonlyArray<SelectedRestaurantReservation>) =>
      sortReservationsChronologically(reservations)
        .map((reservation) => {
          const restaurantOptions = getFallbackRestaurantOptionsForDate(reservation.date);
          return (
            restaurantOptions.find(
              (restaurant) => restaurant.code === reservation.restaurantCode
            ) ?? restaurantOptions[0]
          );
        })
        .filter((restaurant) => !!restaurant),
    [getFallbackRestaurantOptionsForDate]
  );

  const getDiningAnalyticsReservationsWithDraft = useCallback(
    (draft: { date?: string; dayNumber?: number; restaurantCode?: string; time?: string } = {}) => {
      const draftRestaurantCode = draft.restaurantCode ?? selectedRestaurantCode;
      const draftTime = draft.time ?? selectedTime;

      if (editingReservationIndex === null || !draftRestaurantCode || !draftTime) {
        return selectedRestaurants;
      }

      const draftDate = draft.date ?? selectedDate;
      if (!draftDate) {
        return selectedRestaurants;
      }

      const currentReservation = selectedRestaurants[editingReservationIndex];
      const itineraryDay = itineraryDays.find((day) => day.date === draftDate);
      const selectedRestaurant = availableDiningRooms.find(
        (room) => room.code === draftRestaurantCode
      );
      const draftReservation: SelectedRestaurantReservation = {
        restaurantCode: draftRestaurantCode,
        restaurantName: selectedRestaurant?.description,
        restaurantCategory: '',
        date: draftDate,
        time: draftTime,
        dayNumber: draft.dayNumber ?? itineraryDay?.dayNumber ?? currentReservation?.dayNumber,
        portName: itineraryDay?.portName ?? currentReservation?.portName,
        isAtSea: itineraryDay?.isAtSea ?? currentReservation?.isAtSea,
      };

      return selectedRestaurants.map((reservation, index) =>
        index === editingReservationIndex ? draftReservation : reservation
      );
    },
    [
      availableDiningRooms,
      editingReservationIndex,
      itineraryDays,
      selectedDate,
      selectedRestaurantCode,
      selectedRestaurants,
      selectedTime,
    ]
  );

  const triggerDiningReservationAnalyticsStep = useCallback(
    (
      stepName: DiningReservationAnalyticsStepName,
      extraData: {
        date?: string;
        selectedDays?: number[];
        availabilityRestaurants?: DiningRestaurantOption[];
        selectedReservations?: ReadonlyArray<SelectedRestaurantReservation>;
        isReservationUpdate?: boolean;
      } = {}
    ) => {
      const sortedSelectedReservations = extraData.selectedReservations
        ? sortReservationsChronologically(extraData.selectedReservations)
        : undefined;

      triggerDiningReservationStepEvent({
        entryPoint: flowConfig.entryPoint,
        isReservationUpdate: extraData.isReservationUpdate ?? editingReservationIndex !== null,
        maximumSlotsBookable,
        bookingNumber: booking?.number,
        itemCode: myAreaOBSCardData?.code,
        itemName: myAreaOBSCardData?.description,
        stepName,
        selectedDay: getDiningAnalyticsSelectedDay(extraData.date),
        selectedDays: extraData.selectedDays,
        availabilityRestaurants: extraData.availabilityRestaurants,
        selectedReservations: sortedSelectedReservations?.map((reservation) => ({
          restaurantCode: reservation.restaurantCode,
          time: reservation.time,
          dayNumber: reservation.dayNumber,
        })),
      });
    },
    [
      booking?.number,
      editingReservationIndex,
      flowConfig.entryPoint,
      getDiningAnalyticsSelectedDay,
      maximumSlotsBookable,
      myAreaOBSCardData?.code,
      myAreaOBSCardData?.description,
    ]
  );

  useEffect(() => {
    const isBeginReservationStep =
      flowConfig.entryPoint === DiningFlowEntryPoint.PRODUCT_CARD &&
      currentStep === DiningStepId.CHOOSE_WHEN_BOOK;

    if (!isBeginReservationStep) {
      hasTrackedBeginReservationPresentation.current = false;
      return;
    }

    if (hasTrackedBeginReservationPresentation.current) {
      return;
    }

    hasTrackedBeginReservationPresentation.current = true;
    triggerDiningReservationAnalyticsStep('begin reservation');
  }, [currentStep, flowConfig, triggerDiningReservationAnalyticsStep]);

  useEffect(() => {
    const isDateTimeSelectionStep =
      currentStep === DiningStepId.DATE_TIME_SELECTION ||
      currentStep === DiningStepId.DATE_SELECTION;

    if (!isDateTimeSelectionStep) {
      hasTrackedDateTimeStepPresentation.current = false;
      return;
    }

    if (editingReservationIndex !== null || hasTrackedDateTimeStepPresentation.current) {
      return;
    }

    hasTrackedDateTimeStepPresentation.current = true;
    triggerDiningReservationAnalyticsStep('select day');
  }, [currentStep, editingReservationIndex, triggerDiningReservationAnalyticsStep]);

  const handleAnalyticsSelectDay = useCallback(
    (date: string, dayNumber: number) => {
      handleSelectDate(date, dayNumber);

      if (!date) {
        return;
      }

      if (
        (flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED ||
          flowConfig.entryPoint === DiningFlowEntryPoint.CART) &&
        editingReservationIndex !== null
      ) {
        const selectedReservations = getDiningAnalyticsReservationsWithDraft({ date, dayNumber });

        triggerDiningReservationAnalyticsStep('select day', {
          date,
          availabilityRestaurants: getFallbackRestaurantOptionsForDate(date),
          selectedReservations,
          isReservationUpdate: true,
        });
        return;
      }

      const analyticsSelectedDays = [...selectedDays, dayNumber];

      triggerDiningReservationAnalyticsStep('select timeslot', {
        date,
        selectedDays: analyticsSelectedDays,
        availabilityRestaurants: getFallbackRestaurantOptionsForDate(date),
      });
    },
    [
      editingReservationIndex,
      flowConfig.entryPoint,
      getDiningAnalyticsReservationsWithDraft,
      getFallbackRestaurantOptionsForDate,
      handleSelectDate,
      selectedDays,
      triggerDiningReservationAnalyticsStep,
    ]
  );

  const handleAnalyticsSelectTime = useCallback(
    (time: string) => {
      handleSelectTime(time);

      if (!time) {
        return;
      }

      if (
        (flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED ||
          flowConfig.entryPoint === DiningFlowEntryPoint.CART) &&
        editingReservationIndex !== null
      ) {
        const selectedReservations = getDiningAnalyticsReservationsWithDraft({ time });

        triggerDiningReservationAnalyticsStep('select timeslot', {
          date: selectedDate,
          availabilityRestaurants: getFallbackRestaurantOptionsForDate(selectedDate),
          selectedReservations,
          isReservationUpdate: true,
        });
      }
    },
    [
      editingReservationIndex,
      flowConfig.entryPoint,
      getDiningAnalyticsReservationsWithDraft,
      getFallbackRestaurantOptionsForDate,
      handleSelectTime,
      selectedDate,
      triggerDiningReservationAnalyticsStep,
    ]
  );

  const handleAnalyticsSelectRestaurantTime = useCallback(
    (restaurantCode: string, time: string) => {
      handleSelectRestaurantTime(restaurantCode, time);

      if (!time) {
        return;
      }

      if (
        (flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED ||
          flowConfig.entryPoint === DiningFlowEntryPoint.CART) &&
        editingReservationIndex !== null
      ) {
        const selectedReservations = getDiningAnalyticsReservationsWithDraft({
          restaurantCode,
          time,
        });

        triggerDiningReservationAnalyticsStep('select timeslot', {
          date: selectedDate,
          availabilityRestaurants: getFallbackRestaurantOptionsForDate(selectedDate),
          selectedReservations,
          isReservationUpdate: true,
        });
      }
    },
    [
      editingReservationIndex,
      flowConfig.entryPoint,
      getDiningAnalyticsReservationsWithDraft,
      getFallbackRestaurantOptionsForDate,
      handleSelectRestaurantTime,
      selectedDate,
      triggerDiningReservationAnalyticsStep,
    ]
  );

  const handleConflictRestaurantTimeSelect = useCallback(
    (_restaurantCode: string, time: string) => {
      const title = diningProps?.diningDateTimeProps?.conflictTimeslotModalTitle;
      const descr = diningProps?.diningDateTimeProps?.conflictTimeslotModalBody;
      if (!title || !descr) return;
      const description = replacePlaceholders(descr, { time });

      openModal({
        title,
        description,
        onConfirm: closeModal,
        onConfirmLabel: diningProps?.diningDateTimeProps?.conflictTimeslotModalConfirmLabel,
      });
    },
    [
      closeModal,
      diningProps?.diningDateTimeProps?.conflictTimeslotModalBody,
      diningProps?.diningDateTimeProps?.conflictTimeslotModalConfirmLabel,
      diningProps?.diningDateTimeProps?.conflictTimeslotModalTitle,
      openModal,
    ]
  );

  useEffect(() => {
    if (currentStep !== DiningStepId.CHOOSE_WHEN_BOOK) {
      return;
    }

    if (isReserveNowDisabled) {
      if (flowState.reservationMode !== DiningReservationMode.WITHOUT_TIMESLOT) {
        dispatch({
          type: DiningFlowEventType.SELECT_RESERVATION_MODE,
          mode: DiningReservationMode.WITHOUT_TIMESLOT,
        });
      }

      setChooseWhenBookBannerVariant((currentVariant) =>
        currentVariant === ChooseWhenBookBannerVariant.GENERIC
          ? currentVariant
          : ChooseWhenBookBannerVariant.RESERVATION_UNAVAILABLE
      );
    }
  }, [currentStep, flowState.reservationMode, isReserveNowDisabled]);

  useEffect(() => {
    const isDateTimeStep = currentStep === DiningStepId.DATE_TIME_SELECTION;
    const isCartLikeEntryPoint =
      flowConfig.entryPoint === DiningFlowEntryPoint.CART ||
      flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED;

    if (!isDateTimeStep || !isCartLikeEntryPoint) {
      return;
    }

    // CART / ALREADY_PURCHASED can enter DATE_TIME_SELECTION without passing through PAX_SELECTION.
    // Use bootstrap seed state and then run INT136 initial availability request.
    if (hasAttemptedDateTimeBootstrap) {
      return;
    }

    setHasAttemptedDateTimeBootstrap(true);
    void (async () => {
      try {
        // CART may need INT135 for complete diningRoom metadata (e.g., per-restaurant limits).
        // ALREADY_PURCHASED uses INT130 exclusively and does not fetch INT135.
        if (flowConfig.entryPoint === DiningFlowEntryPoint.CART) {
          await prefetchINT135DiningRoomMetadata();
        }
        await fetchInitialThematicAvailability();
      } catch {
        // ErrorManager + availability state are handled by the availability hook.
      }
    })();
  }, [
    currentStep,
    fetchInitialThematicAvailability,
    flowConfig.entryPoint,
    hasAttemptedDateTimeBootstrap,
    prefetchINT135DiningRoomMetadata,
  ]);

  useEffect(() => {
    const isDateStepContext =
      currentStep === DiningStepId.DATE_SELECTION ||
      currentStep === DiningStepId.DATE_TIME_SELECTION ||
      currentStep === DiningStepId.TIME_SELECTION;

    if (!isDateStepContext || !hasInitialAvailabilityServiceErrorFallback || !selectedDate) {
      return;
    }

    // INT136.1 initial failure must leave the day unselected to avoid showing stale room/slot UI.
    handleSelectDate('', 0);
  }, [currentStep, handleSelectDate, hasInitialAvailabilityServiceErrorFallback, selectedDate]);

  // ---------------------------------------------------------------------------
  // Step title resolver — keeps step rendering dumb
  // ---------------------------------------------------------------------------

  const resolveStepTitle = (step: DiningStepId): string => {
    switch (step) {
      case DiningStepId.PAX_SELECTION:
        return '';
      case DiningStepId.DATE_SELECTION:
        return obsTitle ?? '';
      case DiningStepId.TIME_SELECTION:
        return obsTitle ?? '';
      case DiningStepId.CHOOSE_WHEN_BOOK:
        return obsTitle ?? '';
      case DiningStepId.RESERVATION_RECAP:
        return obsTitle ?? '';
      case DiningStepId.CART_RESERVATION_RECAP:
        return obsTitle ?? '';
      case DiningStepId.CONFIRM:
        return obsTitle ?? '';
      case DiningStepId.SUCCESS:
        return obsTitle ?? '';
      default: {
        return obsTitle ?? '';
      }
    }
  };

  const resolveHeaderLayout = (
    step: DiningStepId
  ): 'default' | 'transparent-header' | 'confirmation-drawer' => {
    switch (step) {
      case DiningStepId.PAX_SELECTION:
      case DiningStepId.PAX_SELECTION_EDIT:
        return isCartOrAlreadyPurchasedFlow ? 'confirmation-drawer' : 'transparent-header';
      case DiningStepId.DATE_SELECTION:
        return 'confirmation-drawer';
      case DiningStepId.DATE_TIME_SELECTION:
        return 'confirmation-drawer';
      case DiningStepId.TIME_SELECTION:
        return 'confirmation-drawer';
      case DiningStepId.CHOOSE_WHEN_BOOK:
        return 'confirmation-drawer';
      case DiningStepId.RESERVATION_RECAP:
        return 'confirmation-drawer';
      case DiningStepId.CART_RESERVATION_RECAP:
        return 'confirmation-drawer';
      case DiningStepId.CONFIRM:
        return 'confirmation-drawer';
      case DiningStepId.SUCCESS:
        return 'confirmation-drawer';
      default: {
        return 'default';
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const getDiningSelectionForCart = useCallback(
    (reservationsSource: readonly SelectedRestaurantReservation[] = selectedRestaurants) => {
      return buildDiningSelectionForCartMetadata({
        reservationsSource,
        preselectedReservations: seedContext?.preselectedReservations,
        entryPoint: flowConfig.entryPoint,
        reservationMode: flowState.reservationMode,
        isReserveNowDisabled,
        preselectedReservationId: seedContext?.preselectedReservationId,
        maximumSlotsBookable,
      });
    },
    [
      flowConfig.entryPoint,
      flowState.reservationMode,
      isReserveNowDisabled,
      maximumSlotsBookable,
      seedContext?.preselectedReservationId,
      seedContext?.preselectedReservations,
      selectedRestaurants,
    ]
  );

  // Declare step context early so it's available in dependency arrays
  const isCartReservationRecapStep = currentStep === DiningStepId.CART_RESERVATION_RECAP;
  const hasReservationsAtDrawerOpen = seededSelectedRestaurants.length > 0;
  const isDateSelectionStep =
    currentStep === DiningStepId.DATE_TIME_SELECTION || currentStep === DiningStepId.DATE_SELECTION;
  const isCartLastAllowedReservationFlow =
    flowConfig.entryPoint === DiningFlowEntryPoint.CART &&
    isDateSelectionStep &&
    isAddReservationFlow(currentStep, editingReservationIndex) &&
    selectedRestaurants.length + 1 >= maximumSlotsBookable;

  const isFooterDrivenDateTimeStep =
    isCartLastAllowedReservationFlow ||
    (isDateSelectionStep &&
      flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED &&
      hasReservationsAtDrawerOpen &&
      !isAddReservationFlow(currentStep, editingReservationIndex));

  const isCartLikeDateStepBootstrapPending =
    (currentStep === DiningStepId.DATE_TIME_SELECTION ||
      currentStep === DiningStepId.DATE_SELECTION) &&
    (flowConfig.entryPoint === DiningFlowEntryPoint.CART ||
      flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED) &&
    !hasResolvedInitialThematicAvailability;

  const {
    isPaxSelectionEditStep,
    isCartOrAlreadyPurchasedPaxEditStep,
    hidePaxEditConfirmUntilSelectionChanges,
    handleEditPassengers,
    handlePaxSelectionNext,
  } = useDiningPaxEditNavigation({
    currentStep,
    isCartOrAlreadyPurchasedFlow,
    passengersToAdd,
    preserveSelectionGoingBack,
    selectedRestaurantsCount: selectedRestaurants.length,
    serializePassengersSelection,
    prefetchINT135DiningRoomMetadata,
    resetReservationsForPassengerEdit,
    onReservationModeSelected,
    dispatch,
    setPassengersToAdd,
    setChooseWhenBookBannerVariant,
    setHasAttemptedDateTimeBootstrap,
    setIsDiningButtonLoading,
  });

  const { handlePrimary } = useDiningPrimaryActionCommand({
    isFooterDrivenDateTimeStep,
    handleConfirmDateTimeSelection,
    currentStep,
    reservationMode: flowState.reservationMode,
    setIsProcessing,
    onConfirmPassengers,
    passengersToAdd: passengersToAdd ?? undefined,
    getDiningSelectionForCart,
    onApply,
    setIsConfirmationDrawer,
    maximumSlotsBookable,
    selectedRestaurantsCount: selectedRestaurants.length,
    isDiningButtonLoading,
    setIsDiningButtonLoading,
    handlePaxSelectionNext,
    onPaxSelectionPersisted: setPersistedPassengersSelection,
    setEditedDiningPackage,
  });

  const handleReservationNow = useCallback(() => {
    if (isReserveNowDisabled) {
      return;
    }

    setChooseWhenBookBannerVariant(ChooseWhenBookBannerVariant.NONE);

    dispatch({
      type: DiningFlowEventType.SELECT_RESERVATION_MODE,
      mode: DiningReservationMode.WITH_TIMESLOT,
    });
    onReservationModeSelected?.(DiningReservationMode.WITH_TIMESLOT);
  }, [isReserveNowDisabled, onReservationModeSelected]);

  const handleReservationLater = useCallback(() => {
    dispatch({
      type: DiningFlowEventType.SELECT_RESERVATION_MODE,
      mode: DiningReservationMode.WITHOUT_TIMESLOT,
    });
    onReservationModeSelected?.(DiningReservationMode.WITHOUT_TIMESLOT);
  }, [onReservationModeSelected]);

  const handleReserveNow = useCallback(async () => {
    if (flowState.reservationMode !== DiningReservationMode.WITH_TIMESLOT) {
      return;
    }

    try {
      setLoading(true);
      // INT136.1 must be called once per restaurant code from INT135 — never without a code.
      await fetchInitialThematicAvailability();
      setChooseWhenBookBannerVariant(ChooseWhenBookBannerVariant.NONE);
      dispatch({ type: DiningFlowEventType.NEXT });
    } catch {
      // ErrorManager + reserve-now disable state are handled by the availability hook.
    } finally {
      setLoading(false);
    }
  }, [fetchInitialThematicAvailability, flowState.reservationMode]);

  // ---------------------------------------------------------------------------
  // Derived CTA state
  // ---------------------------------------------------------------------------

  const isReservationSelectionComplete = selectedRestaurants.length >= maximumSlotsBookable;
  const sortedSelectedRestaurants = useMemo(
    () => sortReservationsChronologically(selectedRestaurants),
    [selectedRestaurants]
  );
  // Map from sorted index to original index (handles case where sort order differs from insertion order)
  const sortedIndexToOriginalIndex = useMemo(() => {
    const map = new Map<number, number>();
    sortedSelectedRestaurants.forEach((sortedReservation, sortedIdx) => {
      const originalIdx = selectedRestaurants.findIndex(
        (r) =>
          r.date === sortedReservation.date &&
          r.time === sortedReservation.time &&
          r.restaurantCode === sortedReservation.restaurantCode
      );
      if (originalIdx !== -1) {
        map.set(sortedIdx, originalIdx);
      }
    });
    return map;
  }, [selectedRestaurants, sortedSelectedRestaurants]);

  const cartReservationRecapProps = {
    ...diningProps?.diningCartReservationRecapProps,
    ...cartReservationRecapPropsOverride,
  };
  const cartRecapAddReservationPlaceholder = cartReservationRecapProps?.addReservationLabel;
  const selectedReservationSummaries = sortedSelectedRestaurants.map((reservation) => {
    const itineraryDay =
      itineraryDays.find((day) => day.date === reservation.date) ??
      (reservation.dayNumber
        ? itineraryDays.find((day) => day.dayNumber === reservation.dayNumber)
        : undefined);

    const dayLabel = diningProps?.diningDateTimeProps?.dayLabel ?? '';
    const dayAtSeaLabel = diningProps?.diningDateTimeProps?.dayAtSeaLabel ?? '';
    const dayNumber = itineraryDay?.dayNumber ?? reservation.dayNumber;
    const portLabel = itineraryDay?.isAtSea
      ? dayAtSeaLabel
      : itineraryDay?.portName ?? reservation.portName ?? dayAtSeaLabel;
    const dayPrefix = dayNumber ? `${dayLabel} ${dayNumber}` : dayLabel;
    const shouldShowRestaurantBeforeTime = maximumSlotsBookable > 1;
    const restaurantLabel = `${
      specialityRestaurantsFromCf?.find((resCF) => resCF.roomCode === reservation.restaurantCode)
        ?.restaurantName ||
      reservation.restaurantName ||
      ''
    }, `;

    return {
      value: `${dayPrefix}, ${portLabel}, ${
        shouldShowRestaurantBeforeTime ? restaurantLabel : ''
      } ${formatTime(reservation.time)}`,
    };
  });

  // Wrapper that maps sorted index to original index for edit operations
  const handleEditReservationBySortedIndex = useCallback(
    (sortedIndex: number) => {
      const originalIndex = sortedIndexToOriginalIndex.get(sortedIndex);
      if (originalIndex !== undefined) {
        handleEditReservation(originalIndex);
      }
    },
    [handleEditReservation, sortedIndexToOriginalIndex]
  );

  const hasCartReservationRecapChanges = useMemo(() => {
    if (!isCartOrAlreadyPurchasedFlow) {
      return true;
    }

    const seededPassengers = serializePassengersSelection(seedContext?.preselectedPassengers);
    const currentPassengers = serializePassengersSelection(passengersToAdd);
    const passengersChanged = seededPassengers !== currentPassengers;

    const seededReservations = fingerprintDiningReservationList(seededSelectedRestaurants);
    const currentReservations = fingerprintDiningReservationList(selectedRestaurants);
    const reservationsChanged = seededReservations !== currentReservations;

    return passengersChanged || reservationsChanged;
  }, [
    isCartOrAlreadyPurchasedFlow,
    passengersToAdd,
    seedContext?.preselectedPassengers,
    seededSelectedRestaurants,
    selectedRestaurants,
    serializePassengersSelection,
  ]);

  const matchingCartItem = useMemo(() => {
    if (!myAreaOBSCardData) {
      return undefined;
    }

    return cartItems.find((item) => item.code === myAreaOBSCardData.code);
  }, [cartItems, myAreaOBSCardData]);

  const alreadyBookedPaxID = useMemo(
    () =>
      booking && myAreaOBSCardData
        ? getAlreadyBookedPassengerId(myAreaOBSCardData, booking, false)
        : [],
    [booking, myAreaOBSCardData]
  );

  const { paxHeadsUpMessage } = useDiningPaxHeadsUpMessage({
    entryPoint: flowConfig.entryPoint,
    diningHeadsUpMessagesConfig,
    selectedObsCode: myAreaOBSCardData?.code,
    cartItemCodes: cartItems.map((item) => item.code),
    hasBookedPassengers: alreadyBookedPaxID.length > 0,
  });

  const footerConfig = useDiningDrawerFooterConfig({
    entryPoint: flowConfig.entryPoint,
    currentStep,
    isCartReservationRecapStep,
    isFooterDrivenDateTimeStep,
    isCartOrAlreadyPurchasedPaxEditStep,
    isPaxSelectionEditStep,
    hadReservationsOnEntry: seededSelectedRestaurants.length > 0,
    hasPaxSelectionChanges: (() => {
      // Only compute changes for CART entry point. ALREADY_PURCHASED must never
      // enable footer primary for pax-edit flows, so return false for it.
      if (flowConfig.entryPoint !== DiningFlowEntryPoint.CART) return false;
      const seededPassengers = serializePassengersSelection(persistedPassengersSelection);
      const currentPassengers = serializePassengersSelection(passengersToAdd);
      return seededPassengers !== currentPassengers;
    })(),
    isConfirming,
    isProcessing,
    hasCartReservationRecapChanges,
    isFooterDrivenDateTimeApplyReady,
    selectedDate,
    selectedTime,
    maximumSlotsBookable,
    chooseWhenBookBannerVariant,
    reservationMode: flowState.reservationMode,
    closeDrawerCancelLabel: diningProps?.diningCloseModalProps?.closeDrawerCancelLabel,
    addToCartButtonLabel: drawerExcursionAEMConfig?.addToCartButtonLabel,
    addingToCartButtonLabel: drawerExcursionAEMConfig?.addingToCartButtonLabel,
    addingToCartButtonIcon: drawerExcursionAEMConfig?.addingToCartButtonIcon,
    // pass-through dining CTA props
    cartConfirmButtonLabel: diningProps?.diningCTA?.cartConfirmButtonLabel,
    cartEditButtonLabel: diningProps?.diningCTA?.cartEditButtonLabel,
    cartUpdatingLabel: diningProps?.diningCTA?.cartUpdatingLabel,
    cartUpdatingIcon: diningProps?.diningCTA?.cartUpdatingIcon,
    alreadyPurchasedConfirmButtonLabel: diningProps?.diningCTA?.alreadyPurchasedConfirmButtonLabel,
    alreadyPurchasedEditButtonLabel: diningProps?.diningCTA?.alreadyPurchasedEditButtonLabel,
    alreadyPurchasedUpdatingLabel: diningProps?.diningCTA?.alreadyPurchasedUpdatingLabel,
    alreadyPurchasedUpdatingIcon: diningProps?.diningCTA?.alreadyPurchasedUpdatingIcon,
    isAddReservationFlow: isAddReservationFlow(currentStep, editingReservationIndex),
  });

  const getReservationIndex = useCallback(() => {
    if (editingReservationIndex !== null) {
      return Math.min(editingReservationIndex + 1, maximumSlotsBookable);
    }

    return Math.min(selectedRestaurants.length + 1, maximumSlotsBookable);
  }, [selectedRestaurants, maximumSlotsBookable, editingReservationIndex]);

  const isNotEditableDiningItem =
    currentStep === DiningStepId.PAX_SELECTION &&
    Boolean(alreadyBookedPaxID?.length || matchingCartItem || isPriceTypeNotAllowed(booking));

  const hasSelectedPassengers =
    passengersToAdd !== null &&
    Object.values(passengersToAdd).some((cabin) => Object.values(cabin).some(Boolean));

  // Count unique passengers across cabins (same pax can appear in multiple cabin maps in booking data).
  const selectedPassengersCount = selectedPassengerIds.length;

  // ---------------------------------------------------------------------------
  // Step content
  // ---------------------------------------------------------------------------

  const renderStepContent = () => {
    switch (currentStep) {
      case DiningStepId.PAX_SELECTION:
        return (
          <>
            <ImageTitle
              imageCode={
                (isLayerExcursionOpen ? myAreaExcursionCardData?.code : myAreaOBSCardData?.code) ??
                ''
              }
              booking={booking}
            />
            <PaxSelectionStep
              isDrawerOpen={isDrawerOpen}
              setPassengersToAdd={setPassengersToAdd}
              hasSelectedPassengers={hasSelectedPassengers}
              confirmButtonLabel={diningProps?.diningPaxProps.buttonLabel}
              confirmButtonLoadingLabel={diningProps?.diningPaxProps.buttonLabelProgress}
              diningChildDiscountLabel={diningProps?.diningPaxProps.diningChildDiscountLabel}
              diningChildDiscountIndicatorLabel={
                diningProps?.diningPaxProps.diningChildDiscountIndicatorLabel
              }
              confirmButtonLoadingLabelIcon={diningProps?.diningPaxProps.buttonLabelProgressIcon}
              onNext={() => {
                void handlePaxSelectionNext();
              }}
              preselectedPassengers={passengersToAdd ?? undefined}
              headsUpMessage={paxHeadsUpMessage}
              disableCompletedDining={true}
              reservations={selectedReservationSummaries}
              isDiningButtonLoading={isDiningButtonLoading}
              hideLongDescription={false}
              setIsDiningButtonLoading={setIsDiningButtonLoading}
            />
          </>
        );

      case DiningStepId.DATE_TIME_SELECTION:
        // For backward compatibility, treat as DATE_SELECTION
        return (
          <DateSelectionStep
            sortedIndexToOriginalIndex={sortedIndexToOriginalIndex}
            itineraryDays={itineraryDays}
            availabilitiesByDate={availabilityMap}
            diningDateTimeProps={diningProps.diningDateTimeProps}
            currentReservationIndex={getReservationIndex()}
            maximumSlotsBookable={maximumSlotsBookable}
            restaurantOptions={restaurantOptionsForSelectedDate}
            selectedRestaurantCode={selectedRestaurantCode}
            selectedDate={selectedDate}
            onSelectDay={handleAnalyticsSelectDay}
            selectedTime={selectedTime}
            onSelectTime={handleAnalyticsSelectTime}
            onSelectRestaurantTime={handleAnalyticsSelectRestaurantTime}
            onConflictRestaurantTimeSelect={handleConflictRestaurantTimeSelect}
            timeslots={selectedDayTimeslots}
            restaurantCode={
              (isLayerExcursionOpen ? myAreaExcursionCardData?.code : myAreaOBSCardData?.code) ?? ''
            }
            restaurantName={obsTitle}
            isLoading={isLoadingAvailability || isCartLikeDateStepBootstrapPending}
            availabilityError={availabilityError}
            isServiceErrorNoTimeslotsFallback={hasInitialAvailabilityServiceErrorFallback}
            hideConfirmSelectionCta={isFooterDrivenDateTimeStep}
            onConfirmSelection={() => {
              void handleConfirmDateTimeSelection();
            }}
            isConfirming={isConfirming}
            isCommittingSelection={isCommittingSelection}
            selectedDays={selectedDays}
            editingReservationDayNumber={editingReservationDayNumber}
            isProductEntryPoint={flowConfig.entryPoint === DiningFlowEntryPoint.PRODUCT_CARD}
          />
        );

      case DiningStepId.DATE_SELECTION:
        return (
          <DateSelectionStep
            sortedIndexToOriginalIndex={sortedIndexToOriginalIndex}
            itineraryDays={itineraryDays}
            availabilitiesByDate={availabilityMap}
            diningDateTimeProps={diningProps.diningDateTimeProps}
            maximumSlotsBookable={maximumSlotsBookable}
            selectedDate={selectedDate}
            onSelectDay={handleAnalyticsSelectDay}
            selectedTime={selectedTime}
            onSelectTime={handleAnalyticsSelectTime}
            timeslots={selectedDayTimeslots}
            restaurantCode={
              (isLayerExcursionOpen ? myAreaExcursionCardData?.code : myAreaOBSCardData?.code) ?? ''
            }
            restaurantName={obsTitle}
            isLoading={isLoadingAvailability || isCartLikeDateStepBootstrapPending}
            availabilityError={availabilityError}
            isServiceErrorNoTimeslotsFallback={hasInitialAvailabilityServiceErrorFallback}
            isConfirming={isConfirming}
            isCommittingSelection={isCommittingSelection}
            selectedDays={selectedDays}
            editingReservationDayNumber={editingReservationDayNumber}
            isProductEntryPoint={flowConfig.entryPoint === DiningFlowEntryPoint.PRODUCT_CARD}
          />
        );

      case DiningStepId.TIME_SELECTION:
        // TIME_SELECTION is no longer a separate step; it's now inline within DATE_SELECTION
        // This case is kept for backward compatibility and flow edge cases
        return (
          <TimeSelectionStep
            restaurantCode={
              (isLayerExcursionOpen ? myAreaExcursionCardData?.code : myAreaOBSCardData?.code) ?? ''
            }
            restaurantName={obsTitle}
            diningDateTimeProps={diningProps.diningDateTimeProps}
            timeslots={selectedDayTimeslots}
            selectedTime={selectedTime}
            isSingleSlotLayout={maximumSlotsBookable === 1}
            isInteractionLocked={isConfirming || isCommittingSelection}
            onSelectTime={handleSelectTime}
            isLoading={isLoadingAvailability}
            availabilityError={availabilityError}
            isServiceErrorNoTimeslotsFallback={hasInitialAvailabilityServiceErrorFallback}
          />
        );

      case DiningStepId.CHOOSE_WHEN_BOOK: {
        const cwbProps = diningProps?.diningChooseWhenBookProps;
        return (
          <ChooseWhenBookStep
            reservationMode={flowState.reservationMode}
            isReserveNowDisabled={isReserveNowDisabled}
            onSelectNow={handleReservationNow}
            onSelectLater={handleReservationLater}
            onEditPassengers={handleEditPassengers}
            title={cwbProps?.title}
            reserveNowLabel={cwbProps?.reserveNowLabel}
            reserveLaterLabel={cwbProps?.reserveLaterLabel}
            passengersLabel={cwbProps?.numberOfPassengersLabel}
            editLabel={cwbProps?.editLabel}
            editIcon={cwbProps?.editIcon}
            selectedPassengersCount={selectedPassengersCount}
            infoMessageTitle={resolveChooseWhenBookInfoTitle(chooseWhenBookBannerVariant, cwbProps)}
            infoMessageBody={resolveChooseWhenBookInfoBody(chooseWhenBookBannerVariant, cwbProps)}
            infoMessageIcon={resolveChooseWhenBookInfoIcon(chooseWhenBookBannerVariant, cwbProps)}
            infoMessageBgColor={resolveChooseWhenBookInfoBgColor(
              chooseWhenBookBannerVariant,
              cwbProps
            )}
            infoMessageFontColor={resolveChooseWhenBookInfoFontColor(
              chooseWhenBookBannerVariant,
              cwbProps
            )}
            makeReservationButtonLabel={
              cwbProps?.makeReservationButtonLabel ?? cwbProps?.reserveNowLabel
            }
            onMakeReservation={() => {
              void handleReserveNow();
            }}
            loading={loading}
          />
        );
      }

      case DiningStepId.CONFIRM:
      case DiningStepId.SUCCESS:
        // Parent callback handles confirmation and close.
        return null;

      case DiningStepId.RESERVATION_RECAP: {
        return (
          <ReservationRecapStep
            diningReservationRecapProps={diningProps.diningReservationRecapProps}
            dayLabel={diningProps.diningDateTimeProps.dayLabel}
            dayAtSeaLabel={diningProps.diningDateTimeProps.dayAtSeaLabel}
            selectedRestaurants={selectedRestaurants}
            canAddAnother={!isReservationSelectionComplete}
            isSingleSlotLayout={maximumSlotsBookable === 1}
            isAlreadyPurchasedFlow={
              flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED
            }
            onEdit={handleEditReservationBySortedIndex}
            onRemove={handleRemoveReservation}
            onAddAnother={handleAddAnotherReservation}
          />
        );
      }

      case DiningStepId.CART_RESERVATION_RECAP: {
        return (
          <CartReservationRecapStep
            passengersCount={selectedPassengersCount}
            reservations={selectedReservationSummaries}
            isAlreadyPurchasedFlow={
              flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED
            }
            showAddReservationAction={selectedRestaurants.length < maximumSlotsBookable}
            showPassengerModificationBanner={
              flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED &&
              Boolean(passengerModificationBannerProps?.bannerTitle)
            }
            passengerModificationBannerTitle={passengerModificationBannerProps?.bannerTitle}
            passengerModificationBannerText={passengerModificationBannerProps?.bannerText}
            passengerModificationBannerIcon={passengerModificationBannerProps?.bannerIcon}
            passengerModificationBannerBgColor={
              passengerModificationBannerProps?.bannerBackgroundColor
            }
            passengerModificationBannerFontColor={passengerModificationBannerProps?.bannerFontColor}
            passengersLabel={cartReservationRecapProps?.passengersLabel}
            reservationLabel={cartReservationRecapProps?.reservationLabel}
            addReservationLabel={cartRecapAddReservationPlaceholder}
            editLabel={cartReservationRecapProps?.editLabel}
            addLabel={cartReservationRecapProps?.addLabel}
            editIcon={cartReservationRecapProps?.editIcon}
            addIcon={cartReservationRecapProps?.addIcon}
            onEditPassengers={handleEditPassengers}
            onEditReservation={handleEditReservationBySortedIndex}
            onAddReservation={() => {
              handleAddAnotherReservation();
            }}
          />
        );
      }

      case DiningStepId.PAX_SELECTION_EDIT:
        return (
          <PaxSelectionStep
            isDrawerOpen={isDrawerOpen}
            setPassengersToAdd={setPassengersToAdd}
            hasSelectedPassengers={hasSelectedPassengers}
            confirmButtonLabel={diningProps?.diningPaxProps.buttonLabel}
            confirmButtonLoadingLabel={diningProps?.diningPaxProps.buttonLabelProgress}
            confirmButtonLoadingLabelIcon={diningProps?.diningPaxProps.buttonLabelProgressIcon}
            diningChildDiscountLabel={diningProps?.diningPaxProps.diningChildDiscountLabel}
            diningChildDiscountIndicatorLabel={
              diningProps?.diningPaxProps.diningChildDiscountIndicatorLabel
            }
            onNext={() => {
              void handlePaxSelectionNext();
            }}
            hideInternalCta={hidePaxEditConfirmUntilSelectionChanges}
            preselectedPassengers={passengersToAdd ?? undefined}
            headsUpMessage={paxHeadsUpMessage}
            reservations={selectedReservationSummaries}
            isDiningButtonLoading={isDiningButtonLoading}
            setIsDiningButtonLoading={setIsDiningButtonLoading}
            hideLongDescription={true}
          />
        );
    }
  };

  const onDrawerClose = () => {
    if (flowState.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED) {
      if (modal === undefined) {
        onClose();
        return;
      }
    } else {
      const shouldConfirmClose = selectedPassengerIds.length > 0 && !isNotEditableDiningItem;

      if (shouldConfirmClose) {
        openModal({
          title: diningProps?.diningCloseModalProps?.closeDrawerConfirmationTitle,
          description: diningProps?.diningCloseModalProps?.closeDrawerConfirmationMessage,
          onCancel: closeModal,
          onConfirm: () => {
            closeModal();
            onClose();
            onDrawerAbandonConfirmed?.();
          },
          onCancelLabel: diningProps?.diningCloseModalProps?.closeDrawerCancelLabel,
          onConfirmLabel: diningProps?.diningCloseModalProps?.closeDrawerConfirmLabel,
          onClose: closeModal,
        });
      } else {
        onClose();
      }
    }
  };

  const onClearFunctionality = () => {
    if (flowConfig.entryPoint === DiningFlowEntryPoint.CART) {
      switch (currentStep) {
        case DiningStepId.PAX_SELECTION_EDIT:
        case DiningStepId.DATE_TIME_SELECTION: {
          setIsProcessing(false);
          dispatch({ type: DiningFlowEventType.BACK });
          break;
        }
        default:
          onDrawerClose();
      }
    } else if (flowConfig.entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED) {
      switch (currentStep) {
        case DiningStepId.DATE_TIME_SELECTION: {
          if (
            seedContext?.preselectedReservations &&
            seedContext?.preselectedReservations.length > 0
          ) {
            setIsProcessing(false);
            dispatch({ type: DiningFlowEventType.BACK });
            break;
          } else {
            onDrawerClose();
            break;
          }
        }
        default:
          onDrawerClose();
      }
    } else {
      onDrawerClose();
    }
  };

  const onDrawerApply = () => {
    isNotEditableDiningItem ? onDrawerClose() : handlePrimary();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const resolveFooterStyle = () =>
    flowConfig.entryPoint === DiningFlowEntryPoint.PRODUCT_CARD
      ? '-dining-default'
      : '-dining-cart';

  return (
    <>
      <Drawer
        closeIcon={drawerProps?.closeDrawerIcon}
        firstButtonLabel={
          isNotEditableDiningItem ? drawerProps?.closeDrawerLabel : footerConfig.primaryLabel
        }
        processingFirstButtonLabel={footerConfig.processingPrimaryLabel}
        processingFirstButtonIcon={footerConfig.processingPrimaryIcon}
        secondButtonLabel={footerConfig.secondaryLabel}
        secondButtonDisabled={footerConfig.isSecondaryDisabled()}
        isDrawerOpen={isDrawerOpen}
        onClose={onDrawerClose}
        enableClosingByClickingOutside={true}
        onClear={onClearFunctionality}
        title={resolveStepTitle(currentStep)}
        onApply={onDrawerApply}
        disabled={!isNotEditableDiningItem && !footerConfig.isPrimaryEnabled}
        isProcessing={footerConfig.isPrimaryProcessing}
        stylingOption={resolveHeaderLayout(currentStep)}
        postBookingWrapperID={postBookingWrapperID}
        showPriceInFooter={!isNotEditableDiningItem && showPriceInFooter}
        footerButtonsVariant={'secondary'}
        footerDiningStyle={resolveFooterStyle()}
        diningCartMultistepFlow
        hiddenFooter={isPriceTypeNotAllowed(booking)}
      >
        <div className={styles['dining-multistep']}>{renderStepContent()}</div>
      </Drawer>
      <div
        ref={confirmationTrapRef}
        tabIndex={-1}
      >
        <DiningModal modal={modal} />
      </div>
    </>
  );
};

export default DiningMultistepDrawer;
